import { useQuery } from '@tanstack/react-query';
import { PageContainer, PageTitle, StatusBadge, StatusStrip } from '@dhuttu/ui';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/useAuth';
import { makeApi } from '../api/client';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  pending: 'warning',
  suspended: 'danger',
};

export default function DashboardRoute() {
  const { getAccessToken } = useAuth();
  const api = makeApi(getAccessToken);
  const meQ = useQuery({
    queryKey: ['org', 'me'],
    queryFn: async () => (await api.org.me()).data,
  });
  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: async () => (await api.org.listStores()).data,
  });

  const status = (meQ.data?.org.status ?? 'unknown') as string;

  return (
    <PageContainer>
      <PageTitle
        title="Dashboard"
        subtitle={meQ.data?.org.name ?? ' '}
        right={
          <StatusStrip label="Merchant status">
            <StatusBadge tone={STATUS_TONE[status] ?? 'neutral'}>{status}</StatusBadge>
          </StatusStrip>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Merchant">
          <p className="text-xl font-semibold text-ink">{meQ.data?.org.name ?? '—'}</p>
          <p className="text-xs text-gray-500 mt-1">Owned by {meQ.data?.membership.email ?? '—'}</p>
        </Card>
        <Card title="Stores">
          <p className="text-3xl font-bold text-ink">{storesQ.data?.items.length ?? 0}</p>
          <p className="text-xs text-gray-500 mt-1">stores in this org</p>
        </Card>
        <Card title="Recent activity">
          <p className="text-sm text-gray-500">No recent activity yet.</p>
        </Card>
      </div>
    </PageContainer>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded border border-gray-200 bg-white p-4">
      <h3 className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2">{title}</h3>
      {children}
    </section>
  );
}
